/**
 * Error_Handler.ts
 *
 * Single source of truth for extracting and displaying backend error messages.
 *
 * Backend error shapes handled:
 *   Platform API  → { message: string }  |  { title: string }  |  { errors: [{ message }] }
 *   Suki API      → { message: string }  |  { error: string }
 *   Network error → Error.message
 */

import { showToast } from '../components/toastComponent';

const MAX_LENGTH = 300;
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/* Characters that are safe to display in a toast — blocks HTML/script injection */
const BLOCKED = /<|>|javascript:|data:/i;

function isSafeString(val: unknown): val is string {
  return typeof val === 'string' && val.trim().length > 0 && !BLOCKED.test(val);
}

/**
 * Extracts the most specific error message from an axios error object.
 * Always returns the real backend message when available.
 * Falls back to `fallback` only when no usable message is found.
 */
export function getApiErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!error || typeof error !== 'object') return fallback;

  const err = error as any;
  const data = err?.response?.data;

  /* ── Priority order: pick the most specific message ── */

  // 1. data.message  (most APIs)
  if (isSafeString(data?.message)) return data.message.trim().slice(0, MAX_LENGTH);

  // 2. data.error.message  (sk-catalog-api envelope: { error: { code, message } })
  if (isSafeString(data?.error?.message)) return data.error.message.trim().slice(0, MAX_LENGTH);

  // 3. data.errors[0].message  (validation error arrays)
  const firstError = Array.isArray(data?.errors) ? data.errors[0] : null;
  if (isSafeString(firstError?.message)) return firstError.message.trim().slice(0, MAX_LENGTH);

  // 4. data.title  (Platform API RFC 7807 shape)
  if (isSafeString(data?.title)) return data.title.trim().slice(0, MAX_LENGTH);

  // 5. data.error  (Suki API short error string)
  if (isSafeString(data?.error)) return data.error.trim().slice(0, MAX_LENGTH);

  // 5. Network / JS error message
  if (isSafeString(err?.message)) return err.message.trim().slice(0, MAX_LENGTH);

  return fallback;
}

/**
 * Shows a backend error message in an error toast.
 * Use this in every catch block instead of manually extracting error.response.data.message.
 *
 * @param error   - The caught error (any shape)
 * @param title   - Toast heading (e.g. 'Login Failed', 'Save failed')
 * @param fallback - Optional custom fallback when no backend message is found
 */
export function handleApiError(error: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  const message = getApiErrorMessage(error, fallback);
  showToast('error', title, message);
}
